import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { apiGet, apiPut } from "../services/api";
import PageHeader from "../components/PageHeader";
import AlertMessage from "../components/AlertMessage";

function SettingsPage() {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  const [enableReminders, setEnableReminders] = useState(true);
  const [reminderHours, setReminderHours] = useState(4);

  useEffect(() => {
    loadPreferences();
  }, [user]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setMessage("");
      const data = await apiGet("/Users/notification-preferences");
      setEnableReminders(data.enablePickReminders);
      setReminderHours(data.reminderHoursBeforeLock);
    } catch (error) {
      console.error("Failed to load preferences:", error);
      setMessage("Failed to load notification preferences");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");

      await apiPut("/Users/notification-preferences", {
        enablePickReminders: enableReminders,
        reminderHoursBeforeLock: reminderHours,
      });

      setMessage("Notification preferences updated successfully!");
      setMessageType("success");
    } catch (error) {
      console.error("Failed to save preferences:", error);
      setMessage("Failed to save notification preferences");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 animate-pulse rounded-3xl bg-slate-200" />
        <div className="h-64 animate-pulse rounded-3xl bg-white shadow-sm ring-1 ring-slate-200" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Settings"
        title="Notification Preferences"
        subtitle="Manage how and when you receive email reminders about your picks."
        dark
      />

      <AlertMessage message={message} type={messageType} />

      <div className="bg-white rounded-3xl shadow-sm ring-1 ring-slate-200 p-6 sm:p-8 space-y-6">
        {/* Email Reminders Toggle */}
        <div className="flex items-start justify-between pb-6 border-b border-slate-200">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900">Email Pick Reminders</h3>
            <p className="mt-1 text-sm text-slate-600">
              Get email reminders before gameweeks lock if you haven't submitted your picks yet.
            </p>
          </div>
          <div className="flex items-center gap-3 ml-4">
            <button
              onClick={() => setEnableReminders(!enableReminders)}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                enableReminders ? "bg-emerald-600" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-7 w-7 transform rounded-full bg-white shadow transition-transform ${
                  enableReminders ? "translate-x-6" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Reminder Hours Setting */}
        {enableReminders && (
          <div className="pt-2">
            <label className="block text-sm font-medium text-slate-700 mb-4">
              Remind me how many hours before gameweek lock?
            </label>
            <div className="flex items-center gap-4">
              <div className="flex items-center bg-slate-50 rounded-lg border border-slate-200 p-2">
                <button
                  onClick={() => setReminderHours(Math.max(1, reminderHours - 1))}
                  className="px-3 py-1 text-slate-600 hover:text-slate-900 font-semibold"
                  disabled={reminderHours <= 1}
                >
                  −
                </button>
                <span className="px-6 py-1 text-lg font-semibold text-slate-900 min-w-16 text-center">
                  {reminderHours}
                </span>
                <button
                  onClick={() => setReminderHours(Math.min(24, reminderHours + 1))}
                  className="px-3 py-1 text-slate-600 hover:text-slate-900 font-semibold"
                  disabled={reminderHours >= 24}
                >
                  +
                </button>
              </div>
              <span className="text-sm text-slate-600">
                {reminderHours === 1 ? "1 hour" : `${reminderHours} hours`} before lock
              </span>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              You'll receive one email per gameweek if you haven't submitted all your picks yet.
            </p>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            💡 <strong>Pro Tip:</strong> Even with reminders enabled, you can use the "Copy to Other Leagues" 
            feature to quickly submit picks to multiple leagues at once before the deadline!
          </p>
        </div>

        {/* Save Button */}
        <div className="flex gap-3 pt-6 border-t border-slate-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Preferences"}
          </button>
          <button
            onClick={loadPreferences}
            disabled={saving}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">How Email Reminders Work</h3>
        <ul className="space-y-3 text-sm text-slate-700">
          <li className="flex gap-3">
            <span className="text-emerald-600 font-bold">✓</span>
            <span>You'll receive one email reminder per gameweek across all your leagues</span>
          </li>
          <li className="flex gap-3">
            <span className="text-emerald-600 font-bold">✓</span>
            <span>The email includes a link to your dashboard where you can see which leagues need picks</span>
          </li>
          <li className="flex gap-3">
            <span className="text-emerald-600 font-bold">✓</span>
            <span>Use the "Copy to Other Leagues" feature to quickly copy picks between leagues</span>
          </li>
          <li className="flex gap-3">
            <span className="text-emerald-600 font-bold">✓</span>
            <span>Only users who haven't submitted all their picks will receive the reminder</span>
          </li>
          <li className="flex gap-3">
            <span className="text-emerald-600 font-bold">✓</span>
            <span>You can disable reminders anytime from this settings page</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default SettingsPage;
